import { useEffect, useState, useContext } from "react"
import ToDoList from "./ToDoList";
import Notepad from "./Notepad";
import { Pencil, X, Save, ArrowLeft, UserPlus, Trash2 } from 'lucide-react';
import { useLocation, useNavigate, Link } from "react-router-dom";
import { useSingleProject, updateSingleProject, useUserClients, deleteProject } from "../../hooks/db";
import { userContext } from "../../contexts/userContext";

import Tracks from "./Tracks";
import Comments from "./Comments";

const Project = (props) => {

    //get project id from link state
    const location = useLocation();
    const navigate = useNavigate();
    const projectID = location.state.project_id;
    // console.log("Project ID: ", projectID)

    //initialize project detail states
    const [project, setProject] = useState(null);
    const [tempProject, setTempProject] = useState(null);
    const [dueDate, setDueDate] = useState(null);
    const [startDate, setStartDate] = useState(null);
    const [daysTilDue, setDaysTilDue] = useState(null);

    const [editing, setEditing] = useState(false)
    const [saveHovered, setSaveHovered] = useState(false)
    const [confirmDelete, setConfirmDelete] = useState(false)

    const user_id = useContext(userContext);
    const userClients = useUserClients(user_id[0]);

    //use custom hook to fetch project details
    let fetchedProject = useSingleProject(projectID);

    //Format fetched project fee and paid amounts to numbers
    useEffect(() => {
        if (fetchedProject && fetchedProject[0]){
            let formattedFetchedProject = fetchedProject[0];

            let fee = formattedFetchedProject.fee && typeof formattedFetchedProject.fee === 'string' ? formattedFetchedProject.fee.replaceAll("$", "") : null;
            let feeNumber = fee ? Number(fee) : null;
            formattedFetchedProject.fee = feeNumber;

            let paid = formattedFetchedProject.paid && typeof formattedFetchedProject.paid === 'string' ? formattedFetchedProject.paid.replaceAll("$", "") : null;
            let paidNumber = paid ? Number(paid) : null;
            formattedFetchedProject.paid = paidNumber;

            setProject(formattedFetchedProject)
            setTempProject(formattedFetchedProject);
        }
    },[fetchedProject])

    // Format due date and start date for local time zone display
    useEffect(() => {
        if (project) {
            var offset = new Date().getTimezoneOffset();
            var offset_ms = offset * 60000

            let due_date = new Date(Date.parse(project.due_date)+offset_ms);
            setDueDate(due_date.toLocaleDateString())

            let start_date = new Date(Date.parse(project.start_date)+offset_ms);
            setStartDate(start_date.toLocaleDateString());

            let today = Date.now()-offset_ms;
            console.log((due_date - today)/86400000)
            setDaysTilDue(Math.round((due_date - today)/86400000));
        }
    },[project])

    const saveEditing = () => {
        if (project !== tempProject) {
            console.log("Saving project changes...")
            updateProject(tempProject);
        }
        setEditing(false);
        setSaveHovered(false);
    }

    const cancelEditing = () => {
        setTempProject(project);
        setEditing(false);
        setSaveHovered(false);
    }

    const handleDelete = async () => {
        try {
            await deleteProject(project.project_id);
            navigate(-1);
        } catch (err) {
            console.error("Error deleting project:", err);
        }
    }

    // Update project data and upload to server
    const updateProject = async (updatedProject) => {
        setProject(updatedProject)
        //validdate data format first

        try {
            await updateSingleProject(updatedProject.project_id, updatedProject);
        } catch (err) {
            console.error("Error updating project: ", err);
            return;
        } finally {
            console.log("Updating project...");
        }
    }

    if (project) {
        return (
            <div className="m-auto mt-6 px-10 max-w-[1200px] flex flex-col self-center">
                <button
                    onClick={() => navigate(-1)}
                    className="flex items-center gap-1.5 text-[#666] hover:text-[#ffee00] text-xs uppercase tracking-wider font-medium mb-4 transition-colors duration-200 w-fit"
                >
                    <ArrowLeft size={14}/> Back
                </button>
                <div id="projectTitleRow" className="mb-[15px]">
                    <div className="flex">
                        {editing?
                        <div className="flex flex-col uppercase font-semibold">
                            <div className="text-xs text-[#ffee00]">Title</div>
                            <textarea
                                className="text-sm rounded-md h-[20px] pl-1 w-[100px] font-semibold mr-1"
                                defaultValue={project.title}
                                onChange={(e)=>setTempProject(prev => ({ ...prev, title: e.target.value }))}
                            />
                        </div>
                        :
                            <>"{project.title}" -&nbsp;</>
                        }
                        <span className="text-[#ffee00] text-base uppercase font-bold">
                            {editing ?
                                <div className="flex flex-col uppercase font-semibold gap-1">
                                    <div className="text-xs">Artist</div>
                                    <div className="flex items-center gap-2">
                                        <select
                                            className="text-sm rounded-md pl-1 pr-2 py-0.5 font-semibold bg-[#ddd] text-[#222]"
                                            value={tempProject.client}
                                            onChange={(e) => {
                                                const selected = userClients?.find(c => c.name === e.target.value);
                                                setTempProject(prev => ({
                                                    ...prev,
                                                    client: e.target.value,
                                                    client_id: selected?.client_id ?? prev.client_id
                                                }));
                                            }}
                                        >
                                            {userClients?.map(c => (
                                                <option key={c.client_id} value={c.name}>{c.name}</option>
                                            ))}
                                        </select>
                                        <Link
                                            to="/createproject"
                                            className="flex items-center gap-1 text-[10px] text-[#555] hover:text-[#ffee00] transition-colors duration-200 normal-case font-medium tracking-normal"
                                            title="Add new client"
                                        >
                                            <UserPlus size={13}/> New Client
                                        </Link>
                                    </div>
                                </div>
                            :
                                <Link
                                    to="/client"
                                    state={{ client_id: project.client_id }}
                                    className="hover:underline underline-offset-2"
                                >
                                    {project.client}
                                </Link>
                            }
                        </span>
                    </div>
                    <div className="flex items-center gap-3">
                        {editing ? (
                            <>
                                <div className="relative">
                                    <button
                                        onClick={() => { saveEditing(); setConfirmDelete(false); }}
                                        onMouseEnter={() => setSaveHovered(true)}
                                        onMouseLeave={() => setSaveHovered(false)}
                                        className="transition-colors duration-150"
                                        style={{ color: saveHovered ? '#ffee00' : '#aaa' }}
                                    >
                                        <Save size={18}/>
                                    </button>
                                    <span className={`absolute bottom-full left-1/2 -translate-x-1/2 mb-1.5 px-2 py-0.5 rounded text-[10px] whitespace-nowrap bg-[#222] text-[#ccc] transition-opacity duration-100 pointer-events-none ${saveHovered ? 'opacity-100' : 'opacity-0'}`}>
                                        Save changes
                                    </span>
                                </div>
                                <div className="relative group">
                                    <button
                                        onClick={() => { cancelEditing(); setConfirmDelete(false); }}
                                        className="text-[#aaa] hover:text-red-500 transition-colors duration-150"
                                    >
                                        <X size={18}/>
                                    </button>
                                    <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1.5 px-2 py-0.5 rounded text-[10px] whitespace-nowrap bg-[#222] text-[#ccc] opacity-0 group-hover:opacity-100 transition-opacity duration-100 pointer-events-none">
                                        Cancel changes
                                    </span>
                                </div>
                            </>
                        ) : (
                            <div className="relative group">
                                <button
                                    id="editButton"
                                    onClick={() => setEditing(true)}
                                    className="text-[#aaa] hover:text-[#ffee00] transition-colors duration-150"
                                >
                                    <Pencil size={18}/>
                                </button>
                                <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1.5 px-2 py-0.5 rounded text-[10px] whitespace-nowrap bg-[#222] text-[#ccc] opacity-0 group-hover:opacity-100 transition-opacity duration-100 pointer-events-none">
                                    Edit project
                                </span>
                            </div>
                        )}
                    </div>
                </div>
                <div id="projectDetailsRow" className="gradientBG">
                    <div className="projectSectionHeader">
                        PROJECT DETAILS
                    </div>
                    <div id="projectDetails">
                        <form id="projectStatus">
                            <span>Status : </span>
                            <select
                                defaultValue={project.status}
                                onChange={(e) => editing
                                    ? setTempProject(prev => ({ ...prev, status: e.target.value }))
                                    : updateProject({ ...project, status: e.target.value })
                                }
                            >
                                <option value="Not Started">Not Started</option>
                                <option value="Awaiting Assets">Awaiting Assets</option>
                                <option value="In Progress">In Progress</option>
                                <option value="Awaiting Feedback">Awaiting Feedback</option>
                                <option value="Revising">Revising</option>
                                <option value="Mastering">Mastering</option>
                                <option value="Delivering">Delivering</option>
                                <option value="Complete">Complete</option>
                            </select>
                        </form>

                        <div id="paymentDetails">
                            <div id="projectFee">
                                {editing ?
                                    <div className="flex">
                                    <div className="text-[#ffee00] uppercase font-bold">Fee:&nbsp;</div>$
                                        <input
                                            className="w-[50px] ml-1"
                                            type="number"
                                            defaultValue={project.fee}
                                            onChange={(e)=>setTempProject(prev => ({ ...prev, fee: e.target.value }))}
                                        />
                                    </div>
                                :
                                    <><span>Fee : $</span>{project.fee}</>
                                }
                            </div>
                            <div id="projectPaid">
                                {editing ?
                                    <div className="flex">
                                        <div className="text-[#ffee00] uppercase font-bold">Paid:&nbsp;</div>$
                                        <input
                                            className="w-[50px] ml-1"
                                            type="number"
                                            defaultValue={project.paid}
                                            onChange={(e)=>setTempProject(prev => ({ ...prev, paid: e.target.value }))}
                                        />
                                    </div>
                                :
                                    <><span>Paid : $</span>{project.paid}</>
                                }
                            </div>
                        </div>
                        <div id="projectDates">
                            <div>
                                <span>Started : </span>{startDate}
                            </div>
                            <div id="projectDueDate">
                                {editing ?
                                    <>
                                        <span className="text-[#ffee00] uppercase font-bold">Due:&nbsp;</span>
                                        <input
                                            className="w-[100px] ml-1"
                                            type="date"
                                            defaultValue={(project.due_date).slice(0, 10)}
                                            onChange={(e)=>setTempProject(prev => ({ ...prev, due_date: e.target.value }))}
                                        />
                                    </>
                                :
                                    <>
                                        <span>Due : </span> {dueDate}
                                        {daysTilDue < 0 ?
                                            <sup className="ml-2 px-1 font-bold rounded bg-red-600">
                                                {Math.abs(daysTilDue)} days past due
                                            </sup>
                                            :
                                            daysTilDue != 0 ?
                                                <sup className="ml-2 font-bold">
                                                    in {daysTilDue} days
                                                </sup>
                                                :
                                                <sup className="ml-2 px-1 font-bold rounded bg-orange-500">
                                                    due today
                                                </sup>
                                        }
                                    </>
                                }
                            </div>
                        </div>
                    </div>
                </div>
                <div id="projectOffice" className="w-full">
                    <div className="row2 w-full flex flex-col md:grid grid-cols-2 gap-[15px]">
                        <div className="projectPageTray gradientBG">
                            <Notepad projectID={project.project_id} />
                        </div>
                        <div className="projectPageTray gradientBG min-h-[100px]">
                            <ToDoList project_id={project.project_id}/>
                        </div>
                    </div>
                </div>
                <div className=" gradientBG projectPageTray">
                    <Tracks project_id={project.project_id}/>
                </div>
                <div className="fixed bottom-6 right-12">
                    {editing && confirmDelete ? (
                        <div className="flex items-center gap-2">
                            <span className="text-xs text-[#aaa]">Delete project?</span>
                            <button
                                onClick={handleDelete}
                                className="text-xs px-2 py-0.5 rounded bg-red-700 hover:bg-red-600 text-white transition-colors duration-150"
                            >
                                Confirm
                            </button>
                            <button
                                onClick={() => setConfirmDelete(false)}
                                className="text-xs px-2 py-0.5 rounded bg-[#333] hover:bg-[#444] text-[#aaa] transition-colors duration-150"
                            >
                                Cancel
                            </button>
                        </div>
                    ) : editing ? (
                        <div className="relative group">
                            <button
                                onClick={() => setConfirmDelete(true)}
                                className="text-red-700 hover:text-red-500 transition-colors duration-150"
                            >
                                <Trash2 size={16}/>
                            </button>
                            <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1.5 px-2 py-0.5 rounded text-[10px] whitespace-nowrap bg-[#222] text-[#ccc] opacity-0 group-hover:opacity-100 transition-opacity duration-100 pointer-events-none">
                                Delete project
                            </span>
                        </div>
                    ) : null}
                </div>
            </div>
        )
    } else {
        return (
            <div className="flex justify-center items-center h-screen">
                <h1>Loading Project...</h1>
            </div>
        )
    }
}

export default Project